import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Snackbar,
  Typography,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Divider,
  Table,
  Select,
  Option,
  Sheet,
  Modal,
  ModalDialog,
  CircularProgress,
  Alert,
  ModalClose,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack
} from '@mui/joy';
import { 
  FaSave, 
  FaUndo, 
  FaPaperPlane, 
  FaEye
} from 'react-icons/fa';
import { IconWrapper } from './IconWrapper';
import { sendTestEmail, isSignedIn, previewTestEmail } from '../utils/graphService';
import { 
  EmailTemplates as EmailTemplateUtils,
  COMMON_TEMPLATE_VARIABLES,
  FIRST_SURVEY_APPOINTMENT_REQUEST_VARIABLES,
  getAllTemplateVariables
} from '../utils/emailTemplates';
import { 
  WINDOW_TEMPLATE_VARIABLES,
  WINDOW_ITEM_VARIABLES,
  previewWindowTemplate
} from '../templates/WindowOrderEmailTemplate';
import defaultTemplates from '../data/default-email-templates.json';
import { emailTemplates, type EmailTemplateType, type CalendarEventSettings } from '../lib/emailTemplateService';
import type { Database } from '../types/supabase';
import Checkbox from '@mui/joy/Checkbox';
import { Editor as TinyMCEEditor } from '@tinymce/tinymce-react';
import { useNavigate } from 'react-router-dom';
import { emailActivity } from '../lib/emailActivityService';
import { supabase, directSupabase } from '../lib/supabaseClient';
import { getOrganizationNames } from '../constants/organizations';
import { organisationSettings, sortBrands } from '../services/organisationSettings';

// Template type definitions
export type TemplateType = Database['public']['Tables']['email_templates']['Row']['template_type'] | 'requestReview';

// Template variable definitions
const GLASS_TEMPLATE_VARIABLES = [
  { name: 'orderNumber', description: 'The unique order number' },
  { name: 'orderDate', description: 'The date the order was created' },
  { name: 'customerReference', description: 'Customer-provided reference number' },
  { name: 'subtotal', description: 'Order subtotal before VAT' },
  { name: 'vat', description: 'VAT amount' },
  { name: 'total', description: 'Total order amount' },
  { name: 'customerName', description: 'Customer name' },
  { name: 'customerEmail', description: 'Customer email address' },
  { name: 'customerPhone', description: 'Customer phone number' },
  { name: 'customerCompany', description: 'Customer company name (optional)' },
  { name: 'customerAddress', description: 'Customer address (optional)' },
  { name: 'customerNotes', description: 'Additional notes from customer (optional)' }
];

type EmailTemplate = Database['public']['Tables']['email_templates']['Row'];

// Update the template type to match database schema
type TemplateData = {
  id?: string;
  created_at?: string;
  updated_at?: string;
  template_type: TemplateType;
  template: string;
  subject: string;
  is_default?: boolean;
  is_active?: boolean;
  version?: number;
  organization_name?: string | null;
  calendar_event_settings: CalendarEventSettings | null;
};

type EmailTemplates = Record<TemplateType, TemplateData>;

// Update the template variables type to include sample values
type TemplateVariable = {
  name: string;
  description: string;
  sample?: string;
};

const templateTypeLabel = (type: string) => {
  return type
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .replace(/([a-z])([A-Z])/g, '$1 $2');
};

const isCalendarEventTemplate = (type: TemplateType): boolean => {
  return [
    'firstSurveyAppointmentRequest',
    'firstSurveyAppointmentConfirmation',
    'finalSurveyAppointmentRequest',
    'finalSurveyAppointmentConfirmation',
    'installationDateConfirmation',
    'deliveryDateConfirmation'
  ].includes(type);
};

const replaceTemplateVariables = (template: string, type: TemplateType): string => {
  if (!template) return '';
  
  // Get all variables for this template type
  const variables = getAllTemplateVariables();
  
  // Replace each variable with a sample value
  let result = template;
  variables.forEach(variable => {
    const regex = new RegExp(`{{${variable.name}}}`, 'g');
    result = result.replace(regex, `[${variable.name}]`);
  });
  
  // Special handling for window order templates
  if (type === 'windowOrder') {
    // Replace window items block with sample data
    const windowItemsRegex = /{{#windows}}([\s\S]*?){{\/windows}}/g;
    const windowItemTemplate = template.match(windowItemsRegex)?.[0] || '';
    
    if (windowItemTemplate) {
      const sampleWindows = [
        {
          reference: 'Front Bedroom',
          width: 1200,
          height: 1200,
          style: 'Traditional Style One',
          frameColor: 'White Smooth',
          glassType: 'Clear Float',
          quantity: 1
        },
        {
          reference: 'Kitchen',
          width: 1800,
          height: 1200,
          style: 'Traditional Style Two',
          frameColor: 'White Smooth',
          glassType: 'Clear Float',
          quantity: 1
        }
      ];
      
      const windowItemsContent = sampleWindows.map(window => {
        let itemContent = windowItemTemplate
          .replace('{{#windows}}', '')
          .replace('{{/windows}}', '');
        
        // Replace window item variables
        WINDOW_ITEM_VARIABLES.forEach(variable => {
          const regex = new RegExp(`{{${variable.name}}}`, 'g');
          const value = window[variable.name as keyof typeof window];
          itemContent = itemContent.replace(regex, String(value || `[${variable.name}]`));
        });
        
        return itemContent;
      }).join('\n');
      
      result = result.replace(windowItemsRegex, windowItemsContent);
    }
  }
  
  return result;
};

const initialCalendarEventSettings: CalendarEventSettings = {
  subject: null,
  body: null,
  duration_minutes: null,
  location: null,
  attendees: null
};

const initialTemplates: EmailTemplates = {
  glassOrder: {
    template_type: 'glassOrder',
    template: '',
    subject: '',
    calendar_event_settings: null
  },
  windowOrder: {
    template_type: 'windowOrder',
    template: '',
    subject: '',
    calendar_event_settings: null
  },
  enduranceDoors: {
    template_type: 'enduranceDoors',
    template: '',
    subject: '',
    calendar_event_settings: null
  },
  roofReplacement: {
    template_type: 'roofReplacement',
    template: '',
    subject: '',
    calendar_event_settings: null
  },
  keystoneIntroduction: {
    template_type: 'keystoneIntroduction',
    template: '',
    subject: '',
    calendar_event_settings: null
  },
  firstSurveyAppointmentRequest: {
    template_type: 'firstSurveyAppointmentRequest',
    template: '',
    subject: '',
    calendar_event_settings: null
  },
  firstSurveyAppointmentConfirmation: {
    template_type: 'firstSurveyAppointmentConfirmation',
    template: '',
    subject: '',
    calendar_event_settings: null
  },
  finalSurveyAppointmentRequest: {
    template_type: 'finalSurveyAppointmentRequest',
    template: '',
    subject: '',
    calendar_event_settings: null
  },
  finalSurveyAppointmentConfirmation: {
    template_type: 'finalSurveyAppointmentConfirmation',
    template: '',
    subject: '',
    calendar_event_settings: null
  },
  sendCustomerEstimate: {
    template_type: 'sendCustomerEstimate',
    template: '',
    subject: '',
    calendar_event_settings: null
  },
  sendEstimateQuote: {
    template_type: 'sendEstimateQuote',
    template: '',
    subject: '',
    calendar_event_settings: null
  },
  sendCustomerInvoice: {
    template_type: 'sendCustomerInvoice',
    template: '',
    subject: '',
    calendar_event_settings: null
  },
  orderGoods: {
    template_type: 'orderGoods',
    template: '',
    subject: '',
    calendar_event_settings: null
  },
  installationDateConfirmation: {
    template_type: 'installationDateConfirmation',
    template: '',
    subject: '',
    calendar_event_settings: null
  },
  deliveryDateConfirmation: {
    template_type: 'deliveryDateConfirmation',
    template: '',
    subject: '',
    calendar_event_settings: null
  },
  outstandingPayment: {
    template_type: 'outstandingPayment',
    template: '',
    subject: '',
    calendar_event_settings: null
  },
  depositRequest: {
    template_type: 'depositRequest',
    template: '',
    subject: '',
    calendar_event_settings: null
  },
  notificationEmail: {
    template_type: 'notificationEmail',
    template: '',
    subject: '',
    calendar_event_settings: null
  },
  otherKeystoneServices: {
    template_type: 'otherKeystoneServices',
    template: '',
    subject: '',
    calendar_event_settings: null
  },
  completionEmail: {
    template_type: 'completionEmail',
    template: '',
    subject: '',
    calendar_event_settings: null
  },
  requestReview: {
    template_type: 'requestReview',
    template: '',
    subject: '',
    calendar_event_settings: null
  },
  newsletter: {
    template_type: 'newsletter',
    template: '',
    subject: '',
    calendar_event_settings: null
  }
};

const DefaultDialog = ({ 
  open, 
  onClose, 
  onSave, 
  pendingSave,
  selectedOrganization,
  templateExists
}: {
  open: boolean;
  onClose: () => void;
  onSave: (asDefault: boolean) => void;
  pendingSave: boolean;
  selectedOrganization: string | null;
  templateExists: boolean;
}) => {
  const isNewOrgTemplate = selectedOrganization && !templateExists;
  
  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog>
        <ModalClose />
        <DialogTitle>Save Template</DialogTitle>
        <DialogContent>
          <Typography mb={2}>
            {isNewOrgTemplate 
              ? `This will be the first template for "${selectedOrganization}". Choose how to save:`
              : "Choose how you want to save this template:"
            }
          </Typography>
          <Stack spacing={2}>
            <Button
              variant="solid"
              color="primary"
              onClick={() => onSave(true)}
              disabled={pendingSave}
              startDecorator={pendingSave && <CircularProgress size="sm" />}
            >
              {isNewOrgTemplate 
                ? `Save as Default Template for ${selectedOrganization}`
                : "Save as Default Template"
              }
            </Button>
            <Button
              variant="outlined"
              color="neutral"
              onClick={() => onSave(false)}
              disabled={pendingSave}
              startDecorator={pendingSave && <CircularProgress size="sm" />}
            >
              Save as New Version
            </Button>
          </Stack>
          {isNewOrgTemplate && (
            <Typography level="body-sm" sx={{ mt: 2, color: 'neutral.500' }}>
              💡 <strong>Recommended:</strong> Choose "Save as Default Template" for the first template of an organization. 
              This makes it the default template that will be used for this organization.
            </Typography>
          )}
          {!isNewOrgTemplate && selectedOrganization && (
            <Typography level="body-sm" sx={{ mt: 2, color: 'neutral.500' }}>
              • <strong>Save as Default:</strong> Replaces the existing default template for this organization
              <br />
              • <strong>Save as New Version:</strong> Creates a new active version while keeping the existing default
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="plain" color="neutral" onClick={onClose}>
            Cancel
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
};

const UnifiedEmailTemplateEditor: React.FC = () => {
  const [templates, setTemplates] = useState<EmailTemplates | null>(null);
  const [selectedTemplateType, setSelectedTemplateType] = useState<TemplateType>('glassOrder');
  const [selectedTemplateVersion, setSelectedTemplateVersion] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [pendingSave, setPendingSave] = useState<boolean>(false);
  const [testEmailDialogOpen, setTestEmailDialogOpen] = useState(false);
  const [testEmailRecipient, setTestEmailRecipient] = useState('');
  const [testEmailSending, setTestEmailSending] = useState(false);
  const [testEmailError, setTestEmailError] = useState<string | null>(null);
  const [testEmailSuccess, setTestEmailSuccess] = useState(false);
  const [testEmailStatus, setTestEmailStatus] = useState<'success' | 'error'>('success');
  const [initialTemplates, setInitialTemplates] = useState<EmailTemplates>({} as EmailTemplates);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedOrganization, setSelectedOrganization] = useState<string | null>(
    () => new URLSearchParams(window.location.search).get('org') || 'Keystone Glazing',
  ); // Admin → Organisations links here with ?org=<name>
  const [organizations, setOrganizations] = useState<string[]>([]);
  const navigate = useNavigate();

  // Brands from Admin → Organisations: those offered for new work, plus any brand that
  // still has templates in use (e.g. Keystone Property while it is retired).
  const loadOrganizations = useCallback(() => {
    organisationSettings.list()
      .then((brands) => {
        const names = sortBrands(brands)
          .filter((brand) => brand.isSelectable || brand.directoryOrgId != null)
          .map((brand) => brand.name);
        setOrganizations(names.length ? names : getOrganizationNames().sort());
      })
      .catch((err) => {
        console.error('Failed to load organizations:', err);
        setOrganizations(getOrganizationNames().sort());
      });
  }, []);

  const loadTemplates = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data, error } = await emailTemplates.getAll(selectedOrganization || undefined);
      if (error) throw error;
      if (!data) throw new Error('No template data received');
      
      const mapped: EmailTemplates = { ...initialTemplates };
      (data as EmailTemplate[]).forEach((tpl) => {
        const templateTyped = tpl as EmailTemplate;
        const templateType = templateTyped.template_type as TemplateType;
        
        // Only load organization-specific templates (no defaults)
        // When an organization is selected, only show templates for that organization
        if (selectedOrganization && templateTyped.organization_name === selectedOrganization) {
          mapped[templateType] = {
            ...templateTyped,
            calendar_event_settings: templateTyped.calendar_event_settings
              ? JSON.parse(JSON.stringify(templateTyped.calendar_event_settings))
              : initialCalendarEventSettings
          };
        } else if (!selectedOrganization && !templateTyped.organization_name) {
          // Only show defaults when no organization is selected
          mapped[templateType] = {
            ...templateTyped,
            calendar_event_settings: templateTyped.calendar_event_settings
              ? JSON.parse(JSON.stringify(templateTyped.calendar_event_settings))
              : initialCalendarEventSettings
          };
        }
      });
      setTemplates(mapped);
      setInitialTemplates(mapped);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load templates');
    } finally {
      setIsLoading(false);
    }
  }, [selectedOrganization]);

  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  useEffect(() => {
    // Always load templates when organization changes (default is set on mount)
    if (selectedOrganization) {
      loadTemplates();
    }
  }, [selectedOrganization, loadTemplates]);

  const handleTemplateTypeChange = (_: React.SyntheticEvent | null, newType: TemplateType) => {
    setSelectedTemplateType(newType);
    setTemplates(prev => {
      if (!prev) return prev;
      const currentTemplate = prev[newType];
      if (!currentTemplate?.calendar_event_settings) {
        return {
          ...prev,
          [newType]: {
            ...currentTemplate,
            calendar_event_settings: initialCalendarEventSettings
          } as TemplateData
        };
      }
      return prev;
    });
  };

  const handleSave = async (asDefault: boolean) => {
    setPendingSave(true);
    setError(null);
    try {
      if (!templates) throw new Error('No templates loaded');
      
      const currentTemplate = templates[selectedTemplateType];
      if (!currentTemplate) throw new Error('No template selected');

      const templateData = {
        template_type: selectedTemplateType,
        subject: currentTemplate.subject,
        template: currentTemplate.template,
        calendar_event_settings: currentTemplate.calendar_event_settings
          ? JSON.parse(JSON.stringify(currentTemplate.calendar_event_settings))
          : null,
        organization_name: selectedOrganization || null
      } as any;

      const { error } = await emailTemplates.save(templateData, asDefault);
      
      if (error) {
        throw new Error(error);
      }

      setSaveDialogOpen(false);
      await loadTemplates();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save template');
    } finally {
      setPendingSave(false);
    }
  };

  const handleRestoreDefaults = async () => {
    if (!window.confirm('Are you sure you want to restore the default email template? This will overwrite your current template.')) return;
    setIsLoading(true);
    setError(null);
    try {
      const { error } = await emailTemplates.restoreDefault(selectedTemplateType);
      if (error) {
        setError('Failed to restore default template');
        setTestEmailError('Failed to restore default template');
        setTestEmailStatus('error');
      } else {
        setTestEmailError('Default template restored');
        setTestEmailStatus('success');
        // Reload templates
        const { data } = await emailTemplates.getAll();
        if (data) {
          const mapped: EmailTemplates = { ...initialTemplates };
          (data as EmailTemplate[]).forEach((tpl) => {
            const templateTyped = tpl as EmailTemplate;
            mapped[templateTyped.template_type as TemplateType] = {
              ...templateTyped,
              calendar_event_settings: templateTyped.calendar_event_settings
                ? JSON.parse(JSON.stringify(templateTyped.calendar_event_settings))
                : initialCalendarEventSettings
            };
          });
          setTemplates(mapped);
        }
      }
    } catch {
      setError('Failed to restore default template');
      setTestEmailError('Failed to restore default template');
      setTestEmailStatus('error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestEmail = async () => {
    if (!templates) return;
    const currentTemplate = templates[selectedTemplateType];
    if (!currentTemplate) return;

    setTestEmailSending(true);
    setTestEmailError(null);
    setTestEmailSuccess(false);

    try {
      // Update the template before sending test email
      const { error: updateError } = await emailTemplates.create({
        template_type: selectedTemplateType,
        subject: currentTemplate.subject,
        template: currentTemplate.template,
        version: 0,
        is_default: false,
        is_active: true,
        calendar_event_settings: currentTemplate.calendar_event_settings
          ? JSON.parse(JSON.stringify(currentTemplate.calendar_event_settings))
          : null
      });

      if (updateError) throw updateError;

      await sendTestEmail(selectedTemplateType as EmailTemplateType, testEmailRecipient, selectedOrganization);
      setTestEmailSuccess(true);
      setTestEmailDialogOpen(false);
    } catch (err) {
      setTestEmailError(err instanceof Error ? err.message : 'Failed to send test email');
    } finally {
      setTestEmailSending(false);
    }
  };

  const handlePreviewEmail = () => {
    if (!templates) return;
    const currentTemplate = templates[selectedTemplateType];
    if (!currentTemplate) return;
    
    const previewContent = replaceTemplateVariables(
      currentTemplate.template,
      selectedTemplateType
    );
    
    const previewSubject = replaceTemplateVariables(
      currentTemplate.subject,
      selectedTemplateType
    );

    // Create a new window with the preview
    const previewWindow = window.open('', '_blank');
    if (previewWindow) {
      previewWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Email Preview - ${previewSubject}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .preview-header { 
              background: #f5f5f5; 
              padding: 15px; 
              margin-bottom: 20px; 
              border-radius: 4px;
            }
            .preview-subject { 
              font-size: 18px; 
              font-weight: bold; 
              margin-bottom: 10px; 
            }
            .preview-content { 
              line-height: 1.6; 
            }
          </style>
        </head>
        <body>
          <div class="preview-header">
            <div class="preview-subject">Subject: ${previewSubject}</div>
          </div>
          <div class="preview-content">
            ${previewContent}
          </div>
        </body>
        </html>
      `);
      previewWindow.document.close();
    }
  };

  const getTemplateVariables = (type: TemplateType) => {
    switch (type) {
      case 'glassOrder':
        return GLASS_TEMPLATE_VARIABLES;
      case 'windowOrder':
        return WINDOW_TEMPLATE_VARIABLES;
      case 'firstSurveyAppointmentRequest':
        return FIRST_SURVEY_APPOINTMENT_REQUEST_VARIABLES;
      case 'depositRequest':
        return [
          { name: 'invoiceRef', description: 'Invoice reference' },
          { name: 'invoiceStatus', description: 'Invoice payment status' },
          { name: 'depositAmount', description: 'Deposit amount requested' },
          { name: 'depositPercentage', description: 'Deposit percentage (number without % sign)' },
          { name: 'amountOutstanding', description: 'Remaining balance after deposit' },
          { name: 'paymentDueDate', description: 'Payment due date' },
          { name: 'paymentLink', description: 'Payment link URL (if available)' }
        ];
      case 'outstandingPayment':
        return [
          { name: 'invoiceRef', description: 'Invoice reference' },
          { name: 'invoiceStatus', description: 'Invoice payment status' },
          { name: 'amountOutstanding', description: 'Outstanding balance amount to be paid' },
          { name: 'paymentDueDate', description: 'Payment due date' },
          { name: 'paymentLink', description: 'Payment link URL (if available)' }
        ];
      case 'notificationEmail':
        return [
          { name: 'customerName', description: 'Customer full name' },
          { name: 'documentKind', description: 'Document type (Quotation / Estimate / Invoice)' },
          { name: 'lifecycleRef', description: 'Document reference number' },
          { name: 'replyMessage', description: 'Staff message sent to customer' },
          { name: 'secureUrl', description: 'Public document URL (legacy placeholder)' },
          { name: 'shareUrl', description: 'Public document URL alias' },
          { name: 'publicDocUrl', description: 'Public document URL for viewing/replying' },
          { name: 'companyName', description: 'Sending organization name' }
        ];
      default:
        return COMMON_TEMPLATE_VARIABLES;
    }
  };

  const getItemVariables = () => {
    return selectedTemplateType === 'windowOrder' ? WINDOW_ITEM_VARIABLES : [];
  };

  // No-op formatting with TinyMCE

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <Typography level="title-md">Loading email templates...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography level="h4" mb={2}>
        Email Template Editor
      </Typography>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert 
          color={error ? "danger" : "success"}
          variant="soft"
          sx={{ mb: 2 }}
        >
          {error}
        </Alert>
      ) : !templates ? (
        <Alert color="neutral" sx={{ mb: 2 }}>
          Loading templates for {selectedOrganization}...
        </Alert>
      ) : (
        <>
          <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControl sx={{ minWidth: 300 }}>
              <FormLabel>Organization</FormLabel>
              <Select
                value={selectedOrganization || ''}
                onChange={(_, newValue) => {
                  setSelectedOrganization(newValue as string | null);
                }}
                placeholder="Select organization"
              >
                {organizations.map((org) => (
                  <Option key={org} value={org}>
                    {org}
                  </Option>
                ))}
              </Select>
              <Typography level="body-sm" sx={{ mt: 1, color: 'neutral.500' }}>
                {selectedOrganization 
                  ? `Editing templates for ${selectedOrganization}. Only organization-specific templates are shown.`
                  : 'Select an organization to view and edit its templates.'}
              </Typography>
            </FormControl>
            <FormControl sx={{ minWidth: 300 }}>
              <FormLabel>Template Type</FormLabel>
              <Select
                value={selectedTemplateType}
                onChange={(_, newValue) => handleTemplateTypeChange(null, newValue as TemplateType)}
              >
                {/* Show all possible template types, not just loaded ones */}
                {Object.keys(initialTemplates).map((type) => (
                  <Option key={type} value={type}>
                    {templateTypeLabel(type)}
                  </Option>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>Subject</FormLabel>
              <Input
                value={templates[selectedTemplateType]?.subject || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const currentTemplate = templates[selectedTemplateType] || {
                    template_type: selectedTemplateType,
                    template: '',
                    subject: '',
                    calendar_event_settings: null
                  };
                  setTemplates({
                    ...templates,
                    [selectedTemplateType]: {
                      ...currentTemplate,
                      subject: e.target.value
                    }
                  } as EmailTemplates);
                }}
              />
            </FormControl>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>Template (HTML)</FormLabel>
              <Tabs
                value={activeTab}
                onChange={(_, newValue) => setActiveTab(newValue as number)}
                sx={{ mb: 2 }}
              >
                <TabList>
                  <Tab>Design</Tab>
                  <Tab>Source</Tab>
                  <Tab>Variables</Tab>
                  <Tab>Preview</Tab>
                </TabList>
                
                <TabPanel value={0}>
                  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 'sm' }}>
                    <TinyMCEEditor
                      tinymceScriptSrc={'/tinymce/tinymce.min.js'}
                      licenseKey={'gpl'}
                      value={templates[selectedTemplateType]?.template || ''}
                      onEditorChange={(content: string) => {
                        const currentTemplate = templates[selectedTemplateType] || {
                          template_type: selectedTemplateType,
                          template: '',
                          subject: '',
                          calendar_event_settings: null
                        };
                        setTemplates({
                          ...templates,
                          [selectedTemplateType]: {
                            ...currentTemplate,
                            template: content || ''
                          }
                        } as EmailTemplates);
                      }}
                      init={{
                        height: 600,
                        menubar: false,
                        plugins: ['link', 'lists', 'table', 'code'],
                        toolbar: 'undo redo | bold italic underline | alignleft aligncenter alignright | bullist numlist | link table | code',
                        base_url: '/tinymce',
                        suffix: '.min',
                        valid_elements: '*[*]'
                      }}
                    />
                  </Box>
                </TabPanel>
                <TabPanel value={1}>
                  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 'sm' }}>
                    <Textarea
                      minRows={20}
                      maxRows={20}
                      sx={{
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                        lineHeight: 1.4,
                        width: '100%',
                        '& textarea': { overflow: 'auto' }
                      }}
                      value={templates[selectedTemplateType]?.template || ''}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                        const currentTemplate = templates[selectedTemplateType] || {
                          template_type: selectedTemplateType,
                          template: '',
                          subject: '',
                          calendar_event_settings: null
                        };
                        setTemplates({
                          ...templates,
                          [selectedTemplateType]: {
                            ...currentTemplate,
                            template: e.target.value
                          }
                        } as EmailTemplates);
                      }}
                    />
                  </Box>
                </TabPanel>
                <TabPanel value={2}>
                  <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 'sm', maxHeight: 500, overflow: 'auto' }}>
                    <Typography level="h4" mb={2}>Available Variables</Typography>
                    <Stack spacing={2}>
                      {getTemplateVariables(selectedTemplateType).map((variable) => (
                        <Box key={variable.name}>
                          <Typography level="title-sm">
                            {`{{${variable.name}}}`}
                          </Typography>
                          <Typography level="body-sm" color="neutral">
                            {variable.description}
                          </Typography>
                        </Box>
                      ))}
                      
                      {selectedTemplateType === 'windowOrder' && (
                        <>
                          <Divider />
                          <Typography level="h4" mb={2}>Window Item Variables</Typography>
                          {WINDOW_ITEM_VARIABLES.map((variable) => (
                            <Box key={variable.name}>
                              <Typography level="title-sm">
                                {`{{${variable.name}}}`}
                              </Typography>
                              <Typography level="body-sm" color="neutral">
                                {variable.description}
                              </Typography>
                            </Box>
                          ))}
                          <Box sx={{ mt: 2 }}>
                            <Typography 
                              level="body-sm"
                              color="neutral"
                            >
                              Use these variables within the {"{{#windows}}"} block to format each window item.
                            </Typography>
                          </Box>
                        </>
                      )}
                    </Stack>
                  </Box>
                </TabPanel>
                <TabPanel value={3}>
                  <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 'sm', maxHeight: 500, overflow: 'auto' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography level="h4">Preview</Typography>
                      <Button
                        variant="outlined"
                        color="neutral"
                        onClick={handlePreviewEmail}
                        startDecorator={<IconWrapper icon={FaEye} />}
                      >
                        Open in New Window
                      </Button>
                    </Box>
                    <Box 
                      sx={{ 
                        p: 2, 
                        bgcolor: 'background.level1',
                        borderRadius: 'sm'
                      }}
                    >
                      <Typography level="title-md" mb={1}>
                        Subject: {replaceTemplateVariables(templates[selectedTemplateType]?.subject || '', selectedTemplateType)}
                      </Typography>
                      <Divider sx={{ my: 2 }} />
                      <Box 
                        sx={{ 
                          '& img': { maxWidth: '100%' },
                          '& table': { 
                            borderCollapse: 'collapse', 
                            width: '100%',
                            '& th, & td': { 
                              border: '1px solid', 
                              borderColor: 'divider', 
                              p: 1 
                            }
                          }
                        }}
                      >
                        <div 
                          dangerouslySetInnerHTML={{ 
                            __html: replaceTemplateVariables(templates[selectedTemplateType]?.template || '', selectedTemplateType) 
                          }} 
                        />
                      </Box>
                    </Box>
                  </Box>
                </TabPanel>
              </Tabs>
            </FormControl>
          </Box>

          {isCalendarEventTemplate(selectedTemplateType) && (
            <Box sx={{ mb: 3 }}>
              <Typography level="h4" mb={2}>
                Calendar Event Settings
              </Typography>
              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Event Subject</FormLabel>
                <Input
                  value={templates[selectedTemplateType]?.calendar_event_settings?.subject || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const currentTemplate = templates[selectedTemplateType] || {
                      template_type: selectedTemplateType,
                      template: '',
                      subject: '',
                      calendar_event_settings: null
                    };
                    setTemplates({
                      ...templates,
                      [selectedTemplateType]: {
                        ...currentTemplate,
                        calendar_event_settings: JSON.parse(JSON.stringify({
                          ...(currentTemplate.calendar_event_settings || {}),
                          subject: e.target.value
                        }))
                      }
                    } as EmailTemplates);
                  }}
                />
              </FormControl>
              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Event Description</FormLabel>
                <Textarea
                  minRows={4}
                  maxRows={8}
                  value={templates[selectedTemplateType]?.calendar_event_settings?.body || ''}
                  onChange={(e) => {
                    const currentTemplate = templates[selectedTemplateType] || {
                      template_type: selectedTemplateType,
                      template: '',
                      subject: '',
                      calendar_event_settings: null
                    };
                    setTemplates({
                      ...templates,
                      [selectedTemplateType]: {
                        ...currentTemplate,
                        calendar_event_settings: JSON.parse(JSON.stringify({
                          ...(currentTemplate.calendar_event_settings || {}),
                          body: e.target.value
                        }))
                      }
                    } as EmailTemplates);
                  }}
                />
              </FormControl>
              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Duration (minutes)</FormLabel>
                <Input
                  type="number"
                  value={templates[selectedTemplateType]?.calendar_event_settings?.duration_minutes || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const currentTemplate = templates[selectedTemplateType] || {
                      template_type: selectedTemplateType,
                      template: '',
                      subject: '',
                      calendar_event_settings: null
                    };
                    setTemplates({
                      ...templates,
                      [selectedTemplateType]: {
                        ...currentTemplate,
                        calendar_event_settings: JSON.parse(JSON.stringify({
                          ...(currentTemplate.calendar_event_settings || {}),
                          duration_minutes: parseInt(e.target.value) || 0
                        }))
                      }
                    } as EmailTemplates);
                  }}
                />
              </FormControl>
              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Location</FormLabel>
                <Input
                  value={templates[selectedTemplateType]?.calendar_event_settings?.location || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const currentTemplate = templates[selectedTemplateType] || {
                      template_type: selectedTemplateType,
                      template: '',
                      subject: '',
                      calendar_event_settings: null
                    };
                    setTemplates({
                      ...templates,
                      [selectedTemplateType]: {
                        ...currentTemplate,
                        calendar_event_settings: JSON.parse(JSON.stringify({
                          ...(currentTemplate.calendar_event_settings || {}),
                          location: e.target.value
                        }))
                      }
                    } as EmailTemplates);
                  }}
                />
              </FormControl>
              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Attendees (one per line)</FormLabel>
                <Textarea
                  minRows={2}
                  maxRows={4}
                  value={templates[selectedTemplateType]?.calendar_event_settings?.attendees?.join('\n') || ''}
                  onChange={(e) => {
                    const currentTemplate = templates[selectedTemplateType] || {
                      template_type: selectedTemplateType,
                      template: '',
                      subject: '',
                      calendar_event_settings: null
                    };
                    setTemplates({
                      ...templates,
                      [selectedTemplateType]: {
                        ...currentTemplate,
                        calendar_event_settings: JSON.parse(JSON.stringify({
                          ...(currentTemplate.calendar_event_settings || {}),
                          attendees: e.target.value.split('\n').filter(Boolean).map(email => ({
                            emailAddress: {
                              address: email,
                              name: email
                            },
                            type: 'required'
                          }))
                        }))
                      }
                    } as EmailTemplates);
                  }}
                />
              </FormControl>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              color="danger"
              onClick={handleRestoreDefaults}
              startDecorator={<IconWrapper icon={FaUndo} />}
              disabled={pendingSave}
            >
              Restore Default
            </Button>
            <Button
              variant="solid"
              color="primary"
              onClick={() => setSaveDialogOpen(true)}
              disabled={pendingSave}
              startDecorator={<IconWrapper icon={FaSave} />}
            >
              Save Template
            </Button>
          </Box>
        </>
      )}

      <DefaultDialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        onSave={handleSave}
        pendingSave={pendingSave}
        selectedOrganization={selectedOrganization}
        templateExists={templates?.[selectedTemplateType]?.organization_name === selectedOrganization}
      />

      <Snackbar
        open={testEmailDialogOpen}
        autoHideDuration={6000}
        onClose={() => setTestEmailDialogOpen(false)}
      >
        <Alert
          color={testEmailStatus === 'error' ? "danger" : "success"}
          variant="soft"
          sx={{ width: '100%' }}
        >
          {testEmailError}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UnifiedEmailTemplateEditor;